import React from "react";
import { Card, Row, Col, List, Button as AntButton } from "antd";
import {
  DeleteOutlined,
  PaperClipOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import Button from "../../Helpers/Button/Button";
import trans from "../../../utils";
import { useFormikContext } from "formik";
import { Input, TextAreaInput, InputDate } from "../../Helpers/Input/Input";
import { InputSelect } from "../../Helpers/Input/RefactoredInput";
import FileUploader from "../../Helpers/Dropzone/FileUploader";
import {
  useGetSubsystemsQuery,
  useGetDisciplinesQuery,
  useGetItemsQuery,
  useGetAcceptanceCriteriaQuery,
  useGetInspectionMethodsQuery,
} from "../../../Redux/service/Snag/Snag";
import { AreaRoomOptions } from "../../../constants";
import dayjs from "dayjs";
import { useTeams } from "../../../hooks/useTeams";
import { useGetCompanyTradesQuery } from "../../../Redux/service/Companies/Company";

const SnagItemRow = ({
  snag,
  index,
  updateSnag,
  removeSnag,
  handleFileChange,
  snagErrors,
  snagTouched,
  sectorsOptions,
  contractorsOptions,
  severitiesOptions,
  contractorsLoading,
  totalSnags,
  isEditMode = false,
}) => {
  const { handleBlur, setFieldTouched, values } = useFormikContext();

  // Fetch subsystems based on selected sector
  const { data: subsystemsData, isFetching: subsystemsLoading } =
    useGetSubsystemsQuery(snag.sector, {
      skip: !snag.sector,
    });

  // Fetch disciplines based on selected subsystem
  // const { data: disciplinesData, isFetching: disciplinesLoading } =
  //   useGetDisciplinesQuery(snag.subsystem, {
  //     skip: !snag.subsystem,
  //   });

  const { data: companyTradesData, isFetching: companyTradesLoading } =
    useGetCompanyTradesQuery();

  const { options: teamOptions } = useTeams({
    projectId: values.project_id,
    zoneId: values.zone_id,
    discipline: snag.discipline,
  });
  // Fetch items based on selected discipline
  const { data: itemsData, isFetching: itemsLoading } = useGetItemsQuery(
    snag.discipline,
    {
      skip: !snag.discipline,
    },
  );

  // Fetch acceptance criteria based on selected item
  const {
    data: acceptanceCriteriaData,
    isFetching: acceptanceCriteriaLoading,
  } = useGetAcceptanceCriteriaQuery(snag.item, {
    skip: !snag.item,
  });

  // Fetch inspection methods based on selected acceptance criteria
  const { data: inspectionMethodsData, isFetching: inspectionMethodsLoading } =
    useGetInspectionMethodsQuery(snag.acceptance_criteria, {
      skip: !snag.acceptance_criteria,
    });

  // Format options
  const subsystemsOptions =
    subsystemsData?.data?.map((subsystem) => ({
      label: subsystem?.label,
      value: subsystem?.value,
    })) || [];

  const disciplinesOptions =
    companyTradesData?.data?.map((discipline) => ({
      label: discipline?.name,
      value: discipline?.id,
    })) || [];

  const itemsOptions =
    itemsData?.data?.map((item) => ({
      label: item?.label,
      value: item?.value,
    })) || [];

  const acceptanceCriteriaOptions =
    acceptanceCriteriaData?.data?.map((criteria) => ({
      label: criteria?.label,
      value: criteria?.value,
    })) || [];

  const inspectionMethodsOptions =
    inspectionMethodsData?.data?.map((method) => ({
      label: method?.label,
      value: method?.value,
    })) || [];

  // ✅ Handle removing existing attachment (mark for deletion)
  const handleRemoveExistingAttachment = (attachmentId) => {
    const currentDeleteIds = snag.delete_attachment_ids || [];
    updateSnag(index, "delete_attachment_ids", [
      ...currentDeleteIds,
      attachmentId,
    ]);

    // Filter out the attachment from display
    const updatedAttachments = (snag.existingAttachments || []).filter(
      (att) => att.id !== attachmentId,
    );
    updateSnag(index, "existingAttachments", updatedAttachments);
  };

  return (
    <Card
      key={index}
      size="small"
      style={{
        marginBottom: 16,
        border: "1px solid #d9d9d9",
        borderRadius: 8,
      }}
      title={snag.item ? snag.item : `${trans("Snag")} ${index + 1}`}
      extra={
        !isEditMode && (
          <Button
            type="text"
            icon={<DeleteOutlined />}
            className={"remove-skill"}
            danger
            onClick={() => removeSnag(index)}
            disabled={totalSnags === 1}
          />
        )
      }
    >
      <Row gutter={16}>
        <Col span={12} className="select-z-index">
          <InputSelect
            id={`snags[${index}].sector`}
            name={`snags[${index}].sector`}
            label={trans("Sector")}
            placeholder={trans("Select Sector")}
            options={sectorsOptions}
            value={snag.sector}
            onChange={(val) => {
              updateSnag(index, "sector", val);
              updateSnag(index, "subsystem", "");
              updateSnag(index, "discipline", "");
              updateSnag(index, "item", "");
              updateSnag(index, "acceptance_criteria", "");
              updateSnag(index, "inspection_method", "");
            }}
            onBlur={handleBlur}
            errors={snagErrors.sector}
            touched={snagTouched.sector}
            formstyle="form-input"
          />
        </Col>

        <Col span={12} className="select-z-index">
          <InputSelect
            id={`snags[${index}].subsystem`}
            name={`snags[${index}].subsystem`}
            label={trans("Subsystem")}
            placeholder={trans("Select Subsystem")}
            options={subsystemsOptions}
            value={snag.subsystem}
            onChange={(val) => {
              updateSnag(index, "subsystem", val);
              updateSnag(index, "discipline", "");
              updateSnag(index, "item", "");
              updateSnag(index, "acceptance_criteria", "");
              updateSnag(index, "inspection_method", "");
            }}
            onBlur={handleBlur}
            errors={snagErrors.subsystem}
            touched={snagTouched.subsystem}
            formstyle="form-input"
            disabled={!snag.sector}
            loading={subsystemsLoading}
          />
        </Col>
        <Col span={12} className="select-z-index">
          <InputSelect
            id={`snags[${index}].discipline`}
            name={`snags[${index}].discipline`}
            label={trans("Discipline")}
            placeholder={trans("Select Discipline")}
            options={disciplinesOptions}
            value={snag.discipline}
            onChange={(val) => {
              updateSnag(index, "discipline", val);
              updateSnag(index, "item", "");
              updateSnag(index, "acceptance_criteria", "");
              updateSnag(index, "inspection_method", "");
            }}
            onBlur={handleBlur}
            errors={snagErrors.discipline}
            touched={snagTouched.discipline}
            formstyle="form-input"
            disabled={!snag.subsystem}
            loading={companyTradesLoading}
          />
        </Col>
        {/* <Col span={12} className="select-z-index">
          <InputSelect
            id={`snags[${index}].item`}
            name={`snags[${index}].item`}
            label={trans("Defect")}
            placeholder={trans("Select Item")}
            options={itemsOptions}
            value={snag.item}
            onChange={(val) => {
              updateSnag(index, "item", val);
              updateSnag(index, "acceptance_criteria", "");
              updateSnag(index, "inspection_method", "");
            }}
            onBlur={handleBlur}
            errors={snagErrors.item}
            touched={snagTouched.item}
            formstyle="form-input"
            disabled={!snag.discipline}
            loading={itemsLoading}
          />
        </Col> */}
        <Col span={12} className="select-z-index">
          <InputSelect
            id={`snags[${index}].assigned_team_id`}
            name={`snags[${index}].assigned_team_id`}
            label={trans("Assign To")}
            placeholder={trans("Select Assign To")}
            options={teamOptions}
            // options={contractorsOptions}
            value={snag.assigned_team_id}
            onChange={(val) => updateSnag(index, "assigned_team_id", val)}
            onBlur={handleBlur}
            errors={snagErrors.assigned_team_id}
            touched={snagTouched.assigned_team_id}
            formstyle="form-input"
            disabled={!snag.discipline}
            loading={contractorsLoading}
          />
        </Col>
        <Col span={24}>
          <TextAreaInput
            title={trans("Description")}
            id={`snags[${index}].description`}
            name={`snags[${index}].description`}
            rows={3}
            placeholder={trans("Enter description")}
            value={snag.description}
            onChange={(e) => updateSnag(index, "description", e.target.value)}
            onBlur={handleBlur}
            errors={snagErrors.description}
            touched={snagTouched.description}
          />
        </Col>
        <Col span={12} className="select-z-index">
          <InputSelect
            id={`snags[${index}].severity`}
            name={`snags[${index}].severity`}
            label={trans("Severity")}
            placeholder={trans("Select severity")}
            options={severitiesOptions}
            value={snag.severity}
            onChange={(val) => updateSnag(index, "severity", val)}
            onBlur={handleBlur}
            errors={snagErrors.severity}
            touched={snagTouched.severity}
            formstyle="form-input"
          />
        </Col>
        <Col span={12}>
          <InputDate
            title={trans("Target Date")}
            id={`snags[${index}].target_date`}
            name={`snags[${index}].target_date`}
            onBlur={(value) =>
              setFieldTouched(`snags[${index}].target_date`, true)
            }
            onChange={(value) =>
              updateSnag(
                index,
                "target_date",
                value ? dayjs(value).toISOString() : "",
              )
            }
            value={snag.target_date || ""}
          />
        </Col>
        <Col span={24}>
          <TextAreaInput
            title={trans("Location Details")}
            id={`snags[${index}].location_details`}
            name={`snags[${index}].location_details`}
            rows={2}
            placeholder={trans("Enter location details")}
            value={snag.location_details}
            onChange={(e) =>
              updateSnag(index, "location_details", e.target.value)
            }
          />
        </Col>

        {/* ✅ Display existing attachments in edit mode */}
        {isEditMode &&
          snag.existingAttachments &&
          snag.existingAttachments.length > 0 && (
            <Col span={24}>
              <div style={{ marginBottom: 16 }}>
                <label className="form-label">
                  {trans("Existing Attachments")}
                </label>
                <List
                  size="small"
                  bordered
                  dataSource={snag.existingAttachments}
                  renderItem={(attachment) => (
                    <List.Item
                      actions={[
                        <AntButton
                          type="text"
                          danger
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() =>
                            handleRemoveExistingAttachment(attachment.id)
                          }
                        >
                          {trans("Remove")}
                        </AntButton>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<PaperClipOutlined />}
                        title={
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {attachment.original_name || attachment.filename}
                          </a>
                        }
                        description={`${(attachment.size / 1024).toFixed(2)} KB`}
                      />
                    </List.Item>
                  )}
                />
              </div>
            </Col>
          )}

        <Col span={24} className="mt-3">
          <FileUploader
            file={snag.files}
            onFileSelect={(files) => handleFileChange(index, files)}
            multiple={true}
            sizeMB={25}
            label={
              isEditMode ? trans("Upload New Files") : trans("Upload Files")
            }
          />
        </Col>
      </Row>
    </Card>
  );
};

export default SnagItemRow;
